import { DataType } from 'devextreme/common';
import DataSource from 'devextreme/data/data_source';
import { CollectionHelper, CompareHelper, ConvertHelper } from 'libs/abc/core';
import { ObjectTypeInfoViewModel, QueryObjectsModel } from 'libs/abc/core/data/src';
import { ParameterDefinition } from 'libs/abc/queries/parameters/core/src';
import { _AbcGridColumn } from './abc-grid-column';
import { AbcGridContextMenuManager } from './abc-grid-context-menu-manager';
import { _AbcGridComponent } from './abc-grid.component';

export enum GridColumnDataType {
  Double = 'Double',
  String = 'String',
  Integer32 = 'Integer32',
  Boolean = 'Boolean',
  Date = 'Date',
  Button = 'button',
  DropDownList = 'dropdownlist',
  DateTimeInput = 'datetimeinput',
  ComboBox = 'ComboBox',
  Time = 'Time',
  TimeSpan = 'TimeSpan',
}

export class GridDataContext {
  columnName: string;
  columnDataField: string;
  columnCaption: string;
  columnVisibleIndex: number;
  columnCustomOPath: string;

  constructor() {}
}

export enum AbcGridGroupBy {
  None = 'none',
  Block = 'block',
}

export class ColumnUpdateArgs {
  public column: _AbcGridColumn;
  public visibility: boolean;
  constructor() {}
}

export enum gridOptions {
  groupable = 'groupable',
  columnFilter = 'columnFilter',
  showfilterrow = 'showfilterrow',
  filterPanel = 'filterPanel',
  selectionMode = 'selectionMode',
  exportButton = 'exportButton',
  summaryTotalCount = 'summaryTotalCount',
  summaryTotalSum = 'summaryTotalSum',
  columnResize = 'columnResize',
  columnHeaders = 'columnHeaders',
}

export interface IStorage {
  setItem(key: string, value: any): Promise<void>;
  getItem(key: string): Promise<any>;
}

export class DefaultStorage implements IStorage {
  private storage: Storage;
  constructor(storage?: Storage) {
    this.storage = storage ?? localStorage;
  }

  public async setItem(key: string, value: any) {
    if (value) {
      this.storage.setItem(key, JSON.stringify(value));
    } else {
      this.storage.removeItem(key);
    }
  }

  public async getItem(key: string): Promise<any> {
    const value = this.storage.getItem(key);
    if (value) {
      return JSON.parse(value);
    } else {
      return value;
    }
  }
}

export class StorageSettings {
  private _key: string;
  private _storage: Promise<IStorage | undefined>;
  public includeFiltering: boolean;
  public includeSorting: boolean;
  public includePaging: boolean;
  public keepGlobalLayoutOptions: boolean;

  public get key(): string {
    return this._key;
  }

  public set key(value: string) {
    this._key = value;
    this.gridInstance.syncSettings();
  }

  public get storage(): Promise<IStorage | undefined> {
    return this._storage;
  }

  public set storage(value: Promise<IStorage | undefined>) {
    this._storage = value;
  }

  constructor(private gridInstance: _AbcGridComponent) {
    this.includeFiltering = true;
    this.includeSorting = true;
    this.includePaging = false;
    this.keepGlobalLayoutOptions = true;
  }
}

export interface DataSourceObject {
  readonly dataSource: any[] | DataSource<any, any>;
  readonly gridColumns: _AbcGridColumn[];
  readonly storageKey?: string | undefined;
  readonly storage?: Promise<IStorage | undefined>;
  readonly focusedRowKey?: any;
  readonly getSelectedRowKeys?: (dataRows: any[] | DataSource<any, any>) => any[];
}

export interface PrepareDataSourceObjectArgs {
  readonly grid: _AbcGridComponent;
  readonly dataSourceObject: DataSourceObject;
  getDataSource?: () => Promise<any[] | DataSource<any, any> | undefined>;
}

export function convertDataType(dataType: GridColumnDataType): DataType {
  switch (dataType) {
    case GridColumnDataType.String:
      return 'string';
    case GridColumnDataType.Boolean:
      return 'boolean';
    case GridColumnDataType.DropDownList || GridColumnDataType.ComboBox:
      return 'object';
    case GridColumnDataType.Date:
      return 'date';
    case GridColumnDataType.DateTimeInput:
      return 'datetime';
    case GridColumnDataType.Integer32:
    case GridColumnDataType.Double:
      return 'number';
    case GridColumnDataType.Time:
      return 'datetime';
    case GridColumnDataType.TimeSpan:
      return 'number';
    default:
      return 'string';
  }
}

export class GridSettings {
  readonly getColumns?: (customColumns: CustomQueryColumn[]) => Promise<_AbcGridColumn[]>;
  readonly storageKey?: string | undefined;
  readonly storage?: Promise<IStorage | undefined>;
  readonly focusedRowKey?: any;
  readonly getSelectedRowKeys?: (dataRows: any[]) => any[];
  readonly initialDataSourceIsAlreadySet?: boolean;
}

export class GridAdapter {
  constructor() {}

  public async bindGridToQuery(
    grid: _AbcGridComponent,
    query: QueryObjectsModel,
    settings: GridSettings,
    opathExpressionEditor: OPathExpressionEditor,
    loadParameterDefinitions?: () => Promise<ParameterDefinition[]>,
    saveParameterDefinitions?: (parameterDefinitions: ParameterDefinition[]) => Promise<void>
  ): Promise<CustomQueryColumn[]> {
    const getColumns = settings?.getColumns;
    let storageKey = settings?.storageKey;
    let storage = settings?.storage;
    const focusedRowKey = settings?.focusedRowKey;
    const getSelectedRowKeys = settings?.getSelectedRowKeys;
    const initialDataSourceIsAlreadySet = settings?.initialDataSourceIsAlreadySet;

    grid.getCustomContext = async () => {
      return {
        opathExpressionEditor: opathExpressionEditor,
        objectType: query.ObjectQueries[0]?.ObjectType,
        loadParameterDefinitions: loadParameterDefinitions,
        saveParameterDefinitions: saveParameterDefinitions,
      };
    };

    let columns: _AbcGridColumn[] = grid.columns;
    let loadedCustomColumns: _AbcGridColumn[] = [];
    let newGridColumn: _AbcGridColumn;
    grid.loadCustomColumn = async (customColumn: CustomQueryColumn) => {
      this.ensureCustomColumnInQuery(query, customColumn);
      columns = grid.columns;
      if (!columns) {
        columns = [];
        grid.columns = columns;
      }
      const result = this.ensureCustomColumnInGridColumns(columns, customColumn);
      if (result?.isNew) {
        newGridColumn = result.gridColumn;
      }
      loadedCustomColumns.push(result.gridColumn);
    };
    grid.refreshCustomColumn = async (customColumn: CustomQueryColumn) => {
      await grid.loadCustomColumn(customColumn);

      await this.setGridDataSourceFromQuery(
        grid,
        query,
        async () => columns,
        storageKey,
        focusedRowKey,
        getSelectedRowKeys
      );

      if (newGridColumn) {
        grid.setColumnVisibleIndex(newGridColumn, customColumn.visibleIndex);
      }
    };

    grid.deleteCustomColumn = async (customColumnName: string) => {
      const queryColumnIndex = query.ObjectQueries[0].Columns.findIndex((_) => _.Name === customColumnName);
      if (queryColumnIndex >= 0) {
        CollectionHelper.removeAt(query.ObjectQueries[0].Columns, queryColumnIndex);
      }
      grid.deleteColumn(customColumnName);
      const columns: _AbcGridColumn[] = grid.columns;

      await this.setGridDataSourceFromQuery(
        grid,
        query,
        async () => columns,
        storageKey,
        focusedRowKey,
        getSelectedRowKeys
      );
    };

    if (storage) {
      grid.storageSettings.storage = storage;
    }

    const customColumns: CustomQueryColumn[] = await grid.loadCustomColumnsFromLayout();

    if (getColumns) {
      const currentColumns = await getColumns(customColumns);
      if (currentColumns) {
        for (const c of loadedCustomColumns) {
          if (!currentColumns.find((_) => _.name === c.name)) {
            currentColumns.push(c);
          }
        }
        columns = currentColumns;
        if (initialDataSourceIsAlreadySet) {
          grid.columns = columns;
        }
      }
    }

    if (!initialDataSourceIsAlreadySet) {
      await this.setGridDataSourceFromQuery(
        grid,
        query,
        async () => columns,
        storageKey,
        focusedRowKey,
        getSelectedRowKeys
      );
    }

    return customColumns;
  }

  public static prepareGridColumn(gridColumn: _AbcGridColumn) {
    if (gridColumn && gridColumn.dataType === convertDataType(GridColumnDataType.Boolean)) {
      gridColumn.calculateCellValue = (rowData) => {
        if (!rowData || CompareHelper.isNullorEmpty(rowData[gridColumn.dataField])) {
          return null;
        }
        return ConvertHelper.toBoolean(rowData[gridColumn.dataField]);
      };
    }
  }

  public ensureCustomColumnInQuery(query: QueryObjectsModel, customColumn: CustomQueryColumn): void {
    let queryColumn = query.ObjectQueries[0].Columns.find((_) => _.Name === customColumn.name);
    if (!queryColumn) {
      queryColumn = {
        OPath: customColumn.opath,
        Name: customColumn.name,
      };
      query.ObjectQueries[0].Columns.push(queryColumn);
    }
    queryColumn.OPath = customColumn.opath;
  }

  public ensureCustomColumnInGridColumns(
    columns: _AbcGridColumn[],
    customColumn: CustomQueryColumn
  ): { gridColumn: _AbcGridColumn; isNew: boolean } {
    let newGridColumn: _AbcGridColumn;
    let gridColumn = columns.find((_) => _.dataField === customColumn.name);
    if (!gridColumn) {
      gridColumn = { dataField: customColumn.name, name: customColumn.name };
      gridColumn.visibleIndex = -1;
      columns.push(gridColumn);
      newGridColumn = gridColumn;
    }
    if (customColumn.caption) {
      gridColumn.caption = customColumn.caption;
    }
    gridColumn[AbcGridContextMenuManager.CustomOPathColumnKey] = customColumn.opath;
    GridAdapter.prepareGridColumn(gridColumn);
    return { gridColumn: gridColumn, isNew: !!newGridColumn };
  }

  private async setGridDataSourceFromQuery(
    grid: _AbcGridComponent,
    query: QueryObjectsModel,
    getColumns: () => Promise<_AbcGridColumn[]>,
    storageKey: string,
    focusedRowKey: string,
    getSelectedRowKeys: (dataRows: any[]) => any[]
  ): Promise<void> {
    const gridObjects = ConvertHelper.arrayBufferToObjectArray(
      await grid.abcCore.data.executeReadObjectsQuery(query).toPromise()
    );

    await grid.setDataSource(
      gridObjects,
      getColumns,
      storageKey ?? grid.storageSettings.key,
      focusedRowKey,
      getSelectedRowKeys ? getSelectedRowKeys(gridObjects) : undefined
    );
  }
}

export class CustomQueryColumn {
  name: string;
  opath: string;
  caption: string;
  visibleIndex?: number;
}

export interface OPathExpressionEditor {
  openExpressionEditor(settings: ExpressionEditorSettings): void;
}

export class CustomQueryContext {
  objectType: string | number;
  opathExpressionEditor: OPathExpressionEditor;
  loadParameterDefinitions?: () => Promise<ParameterDefinition[]>;
  saveParameterDefinitions?: (parameterDefinitions: ParameterDefinition[]) => Promise<void>;
}

export class ExpressionEditorSettings {
  objectTypeInfoViewModels: ObjectTypeInfoViewModel[];
  oPath: string;
  editorInstanceSettings: EditorInstanceSettings;
  columnTitle?: string;
  validationType: ValidationType;
  title?: string;
  parameterDefinitions: ParameterDefinition[] = [];

  constructor() {}
}

export class ExpressionEditorResult {
  oPath: string;
  parameterDefinitions: ParameterDefinition[] = [];
  validate: ValidationResult;
  columnTitle?: string;
}

export class EditorInstanceSettings {
  public expressionsEditorResultAction: (expressionEditorResult: ExpressionEditorResult) => Promise<void>;
  public editorId: string;
  public initialValue: string;
  constructor(data: EditorInstanceSettings) {
    Object.assign(this, data);
  }
}

export enum OpenMode {
  NestedFromValueSelectionParameter,
  NestedFromObjectSelectionParameter,
  FirstInstance,
}

export class ValidationResult {
  public RefObjectTypeName: string;
  public Errors: Error[] = [];
  public ValueCount: string;

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
      this.Errors = [];

      data.Errors?.forEach((error) => {
        this.Errors.push(new Error(error));
      });
    }
  }
}

export enum ValidationType {
  Column,
  Query,
  WebArena,
  Others,
}

export enum SummaryItemType {
  count = 'count',
  sum = 'sum',
}
